import { Injectable, Injector } from '@angular/core';
import { IClassification, IClassificationResult, ISaveClassification } from '../../models/classification.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConsultationRecommendationService } from '../consultation-recommendation/consultation-recommendation.service';
import { RecommendationType } from '../../models/consultation-recommendation.model';
import { environment } from '../../../environments/environment';
import { API_ROUTES } from '../../util/route.constants';

const STORAGE_KEY = 'recommendedClassifications';

@Injectable({
  providedIn: 'root'
})
export class ClassificationService {
  private readonly classificationSubject = new BehaviorSubject<IClassification[]>([]);
  classification$ = this.classificationSubject.asObservable();
  private readonly classificationChangedSubject = new Subject<void>();
  classificationChanged$ = this.classificationChangedSubject.asObservable();


  constructor(
    private readonly http: HttpClient,
    private readonly injector: Injector
  ) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      this.classificationSubject.next(JSON.parse(stored));
    }
  }
  notifyOpenDrawer() {
    //to avoid the circular dependency, using injector
    const consultationRecommendationService = this.injector.get(ConsultationRecommendationService);
    consultationRecommendationService.openDrawer(RecommendationType.Classification);
  }

  setClassifications(classifications: IClassification[], isDataFromService = false) {
    this.classificationSubject.next(classifications);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(classifications));

    if (isDataFromService && classifications.length) {
      this.notifyOpenDrawer();
    }
  }

  appendClassifications(classifications: IClassification[], isDataFromService = false) {
    const existingClassifications = [...this.classificationSubject.value];
    const existingCodes = new Set(existingClassifications.map((classification) => classification.code));

    classifications.forEach((classification) => {
      if (!existingCodes.has(classification.code)) {
        existingClassifications.push({
          ...classification,
          checked: classification.checked ?? false,
          isSaved: classification.isSaved ?? false,
        });
        existingCodes.add(classification.code);
      }
    });

    this.setClassifications(existingClassifications, isDataFromService);
  }

  toggleItem(index: number) {
    const items = [...this.classificationSubject.value];
    items[index].checked = !items[index].checked;
    this.setClassifications(items);
  }

  generateClassifications(
    transcript: string,
  ): Observable<{ response: IClassification[] }> {
    return this.http.post<{ response: IClassification[] }>(
      environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_CLASSIFICATION,
      { transcript }
    )
  }

  getClassificationList(searchText: string = ''): Observable<{ response: IClassification[] }> {
    const trimmedSearchText = searchText.trim();
    const options = trimmedSearchText ? { params: { searchText: trimmedSearchText } } : undefined;

    return this.http.get<{ response: IClassification[] }>(
      `${environment.cva_url}${API_ROUTES.CLASSIFICATIONS_LOOKUP}`,
      options
    );
  }

  save(payload: ISaveClassification): Observable<IClassificationResult> {
    const url = `${environment.cva_url}${API_ROUTES.CONDITION}`;
    return this.http.post<IClassificationResult>(url, payload)
  }

  getClassifications() {
    return this.classificationSubject.value;
  }

  notifyClassificationChanged(): void {
    this.classificationChangedSubject.next();
  }

  clearAll() {
    this.setClassifications([]);
  }
}
